import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, 
  QrCode, 
  MessageSquare, 
  CreditCard,
  TrendingUp,
  AlertTriangle,
  Clock,
  RefreshCw,
  Loader2
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { API } from '../../App';

const StatCard = ({ icon: Icon, label, value, subValue, color, loading }) => (
  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-slate-400 text-sm">{label}</p>
        {loading ? (
          <Loader2 className="w-6 h-6 text-slate-500 animate-spin mt-2" />
        ) : (
          <>
            <p className="text-3xl font-bold text-white mt-1">{value}</p>
            {subValue && <p className="text-sm text-slate-500 mt-1">{subValue}</p>}
          </>
        )}
      </div>
      <div className={`p-3 rounded-xl ${color}`}>
        <Icon className="w-6 h-6" />
      </div>
    </div>
  </div>
);

const AdminDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [expiring, setExpiring] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      const [metricsRes, expiringRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers }),
        axios.get(`${API}/admin/expiring-subscriptions`, { headers })
      ]);

      setMetrics(metricsRes.data.metrics);
      setExpiring(expiringRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Dashboard</h1>
            <p className="text-slate-400">Overview of your platform metrics</p>
          </div>
          <button
            onClick={fetchDashboardData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition-all disabled:opacity-50"
            data-testid="refresh-dashboard-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Users}
            label="Total Users"
            value={metrics?.total_users || 0}
            subValue={`${metrics?.paid_users || 0} paid, ${metrics?.free_users || 0} free`}
            color="bg-blue-500/20 text-blue-500"
            loading={loading}
          />
          <StatCard
            icon={QrCode}
            label="Total QR Codes"
            value={metrics?.total_qr_codes || 0}
            subValue={`${metrics?.active_qr_codes || 0} active`}
            color="bg-emerald-500/20 text-emerald-500"
            loading={loading}
          />
          <StatCard
            icon={MessageSquare}
            label="Messages Today"
            value={metrics?.messages_today || 0}
            subValue={`${metrics?.messages_month || 0} this month`}
            color="bg-purple-500/20 text-purple-500"
            loading={loading}
          />
          <StatCard
            icon={CreditCard}
            label="Paid QR Codes"
            value={metrics?.paid_qr || 0}
            subValue={`${metrics?.free_qr || 0} free tier`}
            color="bg-amber-500/20 text-amber-500"
            loading={loading}
          />
        </div>

        {/* Subscription Status */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-500" />
              </div>
              <h3 className="font-semibold text-white">Expiring in 7 Days</h3>
            </div>
            {loading ? (
              <Loader2 className="w-6 h-6 text-slate-500 animate-spin" />
            ) : (
              <p className="text-4xl font-bold text-yellow-500">{expiring?.expiring_7days || 0}</p>
            )}
            <p className="text-sm text-slate-400 mt-2">QR codes need renewal soon</p>
          </div>

          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
              </div>
              <h3 className="font-semibold text-white">Expiring in 3 Days</h3>
            </div>
            {loading ? (
              <Loader2 className="w-6 h-6 text-slate-500 animate-spin" />
            ) : (
              <p className="text-4xl font-bold text-orange-500">{expiring?.expiring_3days || 0}</p>
            )}
            <p className="text-sm text-slate-400 mt-2">Urgent renewal needed</p>
          </div>

          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <TrendingUp className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="font-semibold text-white">Expired</h3>
            </div>
            {loading ? (
              <Loader2 className="w-6 h-6 text-slate-500 animate-spin" />
            ) : (
              <p className="text-4xl font-bold text-red-500">{expiring?.expired || 0}</p>
            )}
            <p className="text-sm text-slate-400 mt-2">Subscriptions ended</p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h3 className="font-semibold text-white mb-4">User Breakdown</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Active Users</span>
                <span className="text-white font-semibold">
                  {loading ? '...' : (metrics?.total_users || 0) - (metrics?.disabled_users || 0)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Disabled Users</span>
                <span className="text-red-400 font-semibold">
                  {loading ? '...' : metrics?.disabled_users || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Paid Subscribers</span>
                <span className="text-emerald-400 font-semibold">
                  {loading ? '...' : metrics?.paid_users || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Free Tier Users</span>
                <span className="text-slate-300 font-semibold">
                  {loading ? '...' : metrics?.free_users || 0}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h3 className="font-semibold text-white mb-4">QR Code Breakdown</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Active QR Codes</span>
                <span className="text-white font-semibold">
                  {loading ? '...' : metrics?.active_qr_codes || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Paid QR Codes</span>
                <span className="text-emerald-400 font-semibold">
                  {loading ? '...' : metrics?.paid_qr || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Expired QR Codes</span>
                <span className="text-slate-300 font-semibold">
                  {loading ? '...' : metrics?.expired_qr || metrics?.free_qr || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Expiring Soon</span>
                <span className="text-yellow-400 font-semibold">
                  {loading ? '...' : metrics?.expiring_soon || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
